import React, { useEffect, useMemo, useState } from "react";
import {
    getCustomers,
    getItems,
    getInvoices,
    addInvoice,
    updateInvoice,
    addItem,
    updateItem,
    deleteItem,
    deleteInvoice
} from "../lib/firestoreService";
import { generateInvoicePdf, generateReceiptPdf } from "../lib/pdf";

const ITEM_CATEGORIES = ["Website", "Printing", "Technical", "Claim", "Other"];

const getInvoiceStatus = (inv) => inv.status || "unpaid";

const getStatusBadgeStyle = (status) => {
    const isPaid = status === "paid";

    return {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        background: isPaid ? "#e8f5e9" : "#fff7d6",
        color: isPaid ? "#1b7f3a" : "#92400e",
        border: `1px solid ${isPaid ? "#a8d5ac" : "#e7ce68"}`
    };
};

export default function Invoices() {

    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [invoices, setInvoices] = useState([]);

    const [customerId, setCustomerId] = useState("");
    const [lineItemId, setLineItemId] = useState("");
    const [qty, setQty] = useState(1);
    const [lines, setLines] = useState([]);
    const [notes, setNotes] = useState("");

    const [editingInvoiceId, setEditingInvoiceId] = useState(null);
    const [editingInvoiceNo, setEditingInvoiceNo] = useState("");

    const [itemForm, setItemForm] = useState({
        name: "",
        price: "",
        category: "Website"
    });

    const [editingItemId, setEditingItemId] = useState(null);

    // ===== LOAD ALL =====
    const loadAll = async () => {
        setCustomers(await getCustomers());
        setItems(await getItems());
        setInvoices(await getInvoices());
    };

    useEffect(() => {
        loadAll();
    }, []);

    const selectedCustomer = useMemo(
        () => customers.find(c => c.id === customerId),
        [customers, customerId]
    );

    const groupedItems = useMemo(() => {
        return ITEM_CATEGORIES.reduce((acc, category) => {
            acc[category] = items.filter(
                it => (it.category || "Other") === category
            );
            return acc;
        }, {});
    }, [items]);

    // ===== ADD LINE =====
    const addLine = () => {
        const it = items.find(x => x.id === lineItemId);
        if (!it) return;

        const q = Math.max(1, parseInt(qty, 10) || 1);

        setLines(prev => [
            ...prev,
            {
                id: crypto.randomUUID(),
                itemId: it.id,
                name: it.name,
                category: it.category || "Other",
                price: Number(it.price),
                qty: q,
                note: ""
            }
        ]);
    };

    const removeLine = (id) => {
        setLines(prev => prev.filter(l => l.id !== id));
    };

    const updateLine = (id, patch) => {
        setLines(prev =>
            prev.map(l =>
                l.id === id ? { ...l, ...patch } : l
            )
        );
    };

    const total = useMemo(
        () => lines.reduce((a, b) => a + b.qty * b.price, 0),
        [lines]
    );

    // ===== CREATE INVOICE =====
    const resetInvoiceBuilder = () => {
        setCustomerId("");
        setLineItemId("");
        setQty(1);
        setLines([]);
        setNotes("");
        setEditingInvoiceId(null);
        setEditingInvoiceNo("");
    };

    const saveInvoice = async () => {
        if (!selectedCustomer) {
            return alert("Sila pilih customer.");
        }

        if (lines.length === 0) {
            return alert("Sila tambah item invoice.");
        }

        const invoiceNo = editingInvoiceId
            ? editingInvoiceNo
            : `ZB-${new Date()
                .toISOString()
                .slice(0, 10)
                .replaceAll("-", "")}-${String(invoices.length + 1).padStart(
                    3,
                    "0"
                )}`;

        const cleanLines = lines.map(line => ({
            ...line,
            qty: Math.max(1, Number(line.qty) || 1),
            price: Math.max(0, Number(line.price) || 0),
            note: line.note || ""
        }));

        const calculatedTotal = cleanLines.reduce(
            (sum, line) => sum + line.qty * line.price,
            0
        );

        const customerSnapshot = {
            id: selectedCustomer.id || customerId,
            name: selectedCustomer.name || "",
            company: selectedCustomer.company || "",
            email: selectedCustomer.email || "",
            phone: selectedCustomer.phone || "",
            address: selectedCustomer.address || ""
        };

        const payload = {
            invoiceNo,
            customerId,
            customer: customerSnapshot,
            lines: cleanLines,
            notes: notes.trim(),
            total: calculatedTotal
        };

        if (!editingInvoiceId) {
            payload.status = "unpaid";
            payload.paidAt = "";
            payload.paymentMethod = "";
            payload.paymentRef = "";
        }

        try {
            if (editingInvoiceId) {
                await updateInvoice(editingInvoiceId, payload);
            } else {
                await addInvoice(payload);
            }

            await generateInvoicePdf(payload);

            alert(
                editingInvoiceId
                    ? `Invoice ${invoiceNo} berjaya dikemas kini.`
                    : `Invoice ${invoiceNo} berjaya disimpan.`
            );

            resetInvoiceBuilder();
            await loadAll();
        } catch (error) {
            console.error("Invoice save error:", error);
            alert("Invoice gagal disimpan. Sila cuba semula.");
        }
    };

    // ===== CATALOGUE FORM =====
    const resetItemForm = () => {
        setItemForm({
            name: "",
            price: "",
            category: "Website"
        });
        setEditingItemId(null);
    };

    const saveCatalogItem = async (e) => {
        e.preventDefault();

        const name = itemForm.name.trim();
        const price = Number(itemForm.price);
        const category = itemForm.category || "Other";

        if (!name) return alert("Sila masukkan nama item.");
        if (!Number.isFinite(price)) return alert("Sila masukkan harga yang sah.");

        const payload = {
            name,
            price,
            category
        };

        if (editingItemId) {
            await updateItem(editingItemId, payload);
        } else {
            await addItem(payload);
        }

        resetItemForm();
        loadAll();
    };

    const editCatalogItem = (item) => {
        setEditingItemId(item.id);
        setItemForm({
            name: item.name || "",
            price: item.price ?? "",
            category: item.category || "Other"
        });
    };

    const handleDeleteItem = async (item) => {
        const ok = window.confirm(
            `Delete item "${item.name}"?\n\nInvoice lama tidak akan terpadam, sebab invoice sudah simpan snapshot item.`
        );

        if (!ok) return;

        await deleteItem(item.id);

        if (editingItemId === item.id) {
            resetItemForm();
        }

        loadAll();
    };

    const handleEditInvoice = inv => {
        if (getInvoiceStatus(inv) === "paid") {
            const ok = window.confirm(
                `Invoice ${inv.invoiceNo} sudah PAID.\n\nEdit invoice paid boleh mengubah rekod bayaran. Teruskan?`
            );

            if (!ok) return;
        }

        const invoiceLines = (inv.lines || []).map(line => ({
            ...line,

            // Pastikan setiap row mempunyai ID untuk React dan proses edit.
            id: line.id || crypto.randomUUID(),

            qty: Math.max(1, Number(line.qty) || 1),
            price: Math.max(0, Number(line.price) || 0),
            note: line.note || ""
        }));

        setEditingInvoiceId(inv.id);
        setEditingInvoiceNo(inv.invoiceNo || "");
        setCustomerId(inv.customerId || inv.customer?.id || "");
        setLines(invoiceLines);
        setNotes(inv.notes || "");
        setLineItemId("");
        setQty(1);

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const cancelInvoiceEdit = () => {
        const ok = window.confirm(
            "Batalkan edit invoice ini? Perubahan yang belum disimpan akan hilang."
        );

        if (!ok) return;

        resetInvoiceBuilder();
    };

    // ===== DELETE INVOICE =====
    const handleDeleteInvoice = async (inv) => {
        const ok = window.confirm(
            `Delete invoice ${inv.invoiceNo}?\nThis action cannot be undone.`
        );

        if (!ok) return;

        await deleteInvoice(inv.id);
        loadAll();
    };

    const handleMarkPaid = async (inv) => {
        const ok = window.confirm(
            `Tandakan invoice ${inv.invoiceNo} sebagai PAID?`
        );

        if (!ok) return;

        const paymentMethod = window.prompt(
            "Payment method? Contoh: Bank Transfer, Cash, FPX",
            inv.paymentMethod || "Bank Transfer"
        );

        if (paymentMethod === null) return;

        const paymentRef = window.prompt(
            "Payment reference? Optional.",
            inv.paymentRef || ""
        );

        if (paymentRef === null) return;

        const paidAt = new Date().toISOString();

        try {
            await updateInvoice(inv.id, {
                status: "paid",
                paidAt,
                paymentMethod: paymentMethod || "",
                paymentRef: paymentRef || ""
            });

            await loadAll();

            await generateReceiptPdf({
                invoiceNo: inv.invoiceNo,
                customer: inv.customer,
                lines: inv.lines || [],
                notes: inv.notes || "",
                total: inv.total || 0,
                paidAt,
                paymentMethod: paymentMethod || "",
                paymentRef: paymentRef || ""
            });
        } catch (error) {
            console.error("Mark paid error:", error);
            alert("Gagal tanda invoice sebagai paid. Sila cuba semula.");
        }
    };

    const handleUnmarkPaid = async (inv) => {
        const ok = window.confirm(
            `Tukar invoice ${inv.invoiceNo} kembali kepada UNPAID?`
        );

        if (!ok) return;

        try {
            await updateInvoice(inv.id, {
                status: "unpaid",
                paidAt: "",
                paymentMethod: "",
                paymentRef: ""
            });

            await loadAll();
        } catch (error) {
            console.error("Unmark paid error:", error);
            alert("Gagal tukar status invoice. Sila cuba semula.");
        }
    };

    const handleDownloadReceipt = async (inv) => {
        if (getInvoiceStatus(inv) !== "paid") {
            return alert("Invoice ini belum ditanda sebagai PAID.");
        }

        await generateReceiptPdf({
            invoiceNo: inv.invoiceNo,
            customer: inv.customer,
            lines: inv.lines || [],
            notes: inv.notes || "",
            total: inv.total || 0,
            paidAt: inv.paidAt || new Date().toISOString(),
            paymentMethod: inv.paymentMethod || "",
            paymentRef: inv.paymentRef || ""
        });
    };

    const handleRegeneratePdf = async (inv) => {
        await generateInvoicePdf({
            invoiceNo: inv.invoiceNo,
            customer: inv.customer,
            lines: inv.lines || [],
            notes: inv.notes || "",
            total: inv.total || 0
        });
    };

    return (
        <div>
            <h2>Invoice</h2>

            <div className="admin-grid-2">
                {/* ===== BUILDER ===== */}
                <div className="admin-card2">
                    <h3>Invoice Builder</h3>

                    {editingInvoiceId && (
                        <div
                            style={{
                                padding: 12,
                                marginBottom: 14,
                                borderRadius: 8,
                                background: "#fff7d6",
                                border: "1px solid #e7ce68"
                            }}
                        >
                            <strong>Sedang edit:</strong> {editingInvoiceNo}
                            <div className="admin-muted" style={{ marginTop: 4 }}>
                                Perubahan akan disimpan pada invoice yang sama.
                            </div>
                        </div>
                    )}

                    <label>Customer</label>
                    <select
                        value={customerId}
                        onChange={e => setCustomerId(e.target.value)}
                    >
                        <option value="">-- pilih customer --</option>
                        {customers.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.name}{c.company ? ` (${c.company})` : ""}
                            </option>
                        ))}
                    </select>

                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            marginTop: 10
                        }}
                    >
                        <select
                            style={{ flex: 1, minWidth: 220 }}
                            value={lineItemId}
                            onChange={e => setLineItemId(e.target.value)}
                        >
                            <option value="">-- pilih item --</option>

                            {ITEM_CATEGORIES.map(category => {
                                const categoryItems = groupedItems[category] || [];
                                if (categoryItems.length === 0) return null;

                                return (
                                    <optgroup key={category} label={category}>
                                        {categoryItems.map(it => (
                                            <option key={it.id} value={it.id}>
                                                {it.name} — RM {Number(it.price).toFixed(0)}
                                            </option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>

                        <input
                            style={{ width: 90 }}
                            type="number"
                            step="1"
                            min="1"
                            value={qty}
                            onChange={e => setQty(e.target.value)}
                        />

                        <button
                            className="admin-btn"
                            type="button"
                            onClick={addLine}
                        >
                            Add
                        </button>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        {lines.length === 0 ? (
                            <div className="admin-muted">Belum ada item.</div>
                        ) : (
                            <div className="admin-table">
                                {lines.map(l => (
                                    <div className="row" key={l.id}>
                                        <div style={{ flex: 1 }}>
                                            <div className="name">{l.name}</div>

                                            <div className="meta">
                                                {l.category || "Other"}
                                            </div>

                                            <div
                                                className="meta"
                                                style={{
                                                    display: "flex",
                                                    gap: 8,
                                                    marginTop: 4,
                                                    alignItems: "center",
                                                    flexWrap: "wrap"
                                                }}
                                            >
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={l.qty}
                                                    style={{ width: 70 }}
                                                    onChange={e =>
                                                        updateLine(l.id, {
                                                            qty: Math.max(
                                                                1,
                                                                parseInt(e.target.value, 10) || 1
                                                            )
                                                        })
                                                    }
                                                />

                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={l.price}
                                                    style={{ width: 110 }}
                                                    onChange={e =>
                                                        updateLine(l.id, {
                                                            price: Number(e.target.value) || 0
                                                        })
                                                    }
                                                />

                                                <span>
                                                    = RM {(l.qty * l.price).toFixed(2)}
                                                </span>
                                            </div>

                                            <textarea
                                                rows={2}
                                                placeholder="Nota item ini (optional)"
                                                value={l.note || ""}
                                                onChange={e =>
                                                    updateLine(l.id, {
                                                        note: e.target.value
                                                    })
                                                }
                                                style={{
                                                    marginTop: 8,
                                                    width: "100%",
                                                    resize: "vertical"
                                                }}
                                            />
                                        </div>

                                        <button
                                            className="admin-btn danger ghost"
                                            type="button"
                                            onClick={() => removeLine(l.id)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <textarea
                        rows={3}
                        placeholder="Notes (optional)"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        style={{ marginTop: 12 }}
                    />

                    <div
                        style={{
                            marginTop: 12,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap"
                        }}
                    >
                        <div>
                            <b>Total:</b> RM {total.toFixed(2)}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap"
                            }}
                        >
                            {editingInvoiceId && (
                                <button
                                    className="admin-btn ghost"
                                    type="button"
                                    onClick={cancelInvoiceEdit}
                                >
                                    Cancel Edit
                                </button>
                            )}

                            <button
                                className="admin-btn"
                                type="button"
                                onClick={saveInvoice}
                            >
                                {editingInvoiceId
                                    ? "Update + Generate PDF"
                                    : "Generate PDF + Simpan"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ===== CATALOGUE ===== */}
                <div className="admin-card2">
                    <h3>Item Catalogue</h3>

                    <form
                        onSubmit={saveCatalogItem}
                        style={{
                            display: "grid",
                            gap: 8,
                            marginBottom: 18
                        }}
                    >
                        <input
                            placeholder="Nama item"
                            value={itemForm.name}
                            onChange={e =>
                                setItemForm({
                                    ...itemForm,
                                    name: e.target.value
                                })
                            }
                        />

                        <input
                            placeholder="Harga RM"
                            type="number"
                            min="0"
                            step="0.01"
                            value={itemForm.price}
                            onChange={e =>
                                setItemForm({
                                    ...itemForm,
                                    price: e.target.value
                                })
                            }
                        />

                        <select
                            value={itemForm.category}
                            onChange={e =>
                                setItemForm({
                                    ...itemForm,
                                    category: e.target.value
                                })
                            }
                        >
                            {ITEM_CATEGORIES.map(category => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </select>

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button className="admin-btn" type="submit">
                                {editingItemId ? "Update Item" : "+ Add Item"}
                            </button>

                            {editingItemId && (
                                <button
                                    className="admin-btn ghost"
                                    type="button"
                                    onClick={resetItemForm}
                                >
                                    Cancel Edit
                                </button>
                            )}
                        </div>
                    </form>

                    {items.length === 0 ? (
                        <div className="admin-muted">Belum ada item catalogue.</div>
                    ) : (
                        <div>
                            {ITEM_CATEGORIES.map(category => {
                                const categoryItems = groupedItems[category] || [];
                                if (categoryItems.length === 0) return null;

                                return (
                                    <div key={category} style={{ marginTop: 16 }}>
                                        <h4
                                            style={{
                                                marginBottom: 8,
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center"
                                            }}
                                        >
                                            <span>{category}</span>
                                            <small className="admin-muted">
                                                {categoryItems.length} item
                                            </small>
                                        </h4>

                                        <div className="admin-table">
                                            {categoryItems.map(it => (
                                                <div className="row" key={it.id}>
                                                    <div>
                                                        <div className="name">{it.name}</div>
                                                        <div className="meta">
                                                            RM {Number(it.price).toFixed(2)}
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            gap: 8,
                                                            flexWrap: "wrap"
                                                        }}
                                                    >
                                                        <button
                                                            className="admin-btn ghost"
                                                            type="button"
                                                            onClick={() => editCatalogItem(it)}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            className="admin-btn danger ghost"
                                                            type="button"
                                                            onClick={() => handleDeleteItem(it)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ===== HISTORY ===== */}
            {/* ===== HISTORY ===== */}
            <div style={{ marginTop: 24 }}>
                <h3>Invoice History</h3>

                {invoices.length === 0 ? (
                    <div className="admin-muted">Belum ada invoice.</div>
                ) : (
                    <div className="admin-table">
                        {invoices.map(inv => (
                            <div className="row" key={inv.id}>
                                <div>
                                    <div className="name">
                                        {inv.invoiceNo} — {inv.customer?.company}
                                    </div>
                                    <div
                                        className="meta"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            flexWrap: "wrap",
                                            marginTop: 4
                                        }}
                                    >
                                        <span>RM {Number(inv.total).toFixed(2)}</span>

                                        <span style={getStatusBadgeStyle(getInvoiceStatus(inv))}>
                                            {getInvoiceStatus(inv) === "paid" ? "PAID" : "UNPAID"}
                                        </span>

                                        {getInvoiceStatus(inv) === "paid" && inv.paidAt && (
                                            <span>
                                                Paid: {new Date(inv.paidAt).toLocaleDateString("ms-MY")}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        flexWrap: "wrap"
                                    }}
                                >
                                    <button
                                        className="admin-btn ghost"
                                        type="button"
                                        onClick={() => handleEditInvoice(inv)}
                                    >
                                        Edit
                                    </button>

                                    <button
                                        className="admin-btn ghost"
                                        type="button"
                                        onClick={() => handleRegeneratePdf(inv)}
                                    >
                                        Invoice PDF
                                    </button>

                                    {getInvoiceStatus(inv) === "paid" ? (
                                        <>
                                            <button
                                                className="admin-btn ghost"
                                                type="button"
                                                onClick={() => handleDownloadReceipt(inv)}
                                            >
                                                Receipt
                                            </button>

                                            <button
                                                className="admin-btn ghost"
                                                type="button"
                                                onClick={() => handleUnmarkPaid(inv)}
                                            >
                                                Unpaid
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="admin-btn"
                                            type="button"
                                            onClick={() => handleMarkPaid(inv)}
                                        >
                                            Mark Paid
                                        </button>
                                    )}

                                    <button
                                        className="admin-btn danger ghost"
                                        type="button"
                                        onClick={() => handleDeleteInvoice(inv)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

